import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { Input as BaseInput, Form, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { map } from 'lodash';
import Fuse from 'fuse.js';

import { ClientSelect } from '../../../appointments/containers/form';
import ServiceSelect from '../../../appointments/components/form/ServiceSelect';
import Calendar from '../../../appointments/containers/form/Calendar';
import { FormLabel as BaseFormLabel } from '../../../../components';
import styles from '../../../../styles';

import { isMongoId } from '../../../../lib/formValidation';
import { priceIsNotValid, isNotNumber } from '../../../../lib/numberFormatter';

const Input = styled(BaseInput)`
    & input {
        background-color: ${props =>
            props.readOnly ? '#fffaf3' : '#fff'} !important;
    }
`;

const Label = styled(BaseFormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const StyledCalendar = styled(Calendar)`
    & .DayPickerInput-OverlayWrapper {
        top: 46px;
    }
`;

const intlMessages = defineMessages({
    serviceTypeError: {
        id: 'pages.schedule.form.AppointmentForm.serviceTypeError',
        defaultMessage: 'Select service type',
    },
    selectClientError: {
        id: 'pages.schedule.form.AppointmentForm.selectClientError',
        defaultMessage: 'Select client OR create a new',
    },
    quantityError: {
        id: 'pages.memberships.form.numberOfUsesError',
        defaultMessage: 'Enter number of uses',
    },
    priceError: {
        id: 'pages.memberships.form.priceError',
        defaultMessage: 'Enter price of the membership',
    },
    serialNumberError: {
        id: 'pages.memberships.form.serialNumberError',
        defaultMessage: 'Enter serial number',
    },
});

function MembershipForm({ intl, onCloseDialog, services, createMode }) {
    const { control, formState: { errors }, setValue } = useFormContext();

    const serviceOptions = useMemo(
        () => map(services, ({ id, name, price, duration }) => ({
            value: id,
            text: name,
            description: price,
            'data-duration': `${duration} min`,
        })),
        [services],
    );

    const onServiceSearch = (items, value) => {
        const fuse = new Fuse(items, { keys: ['text'], shouldSort: true, threshold: 0.3 });
        return fuse.search(value);
    };

    return (
        <Form error={!!errors.root}>
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Group>
                <Form.Field width={12} error={!!errors.client}>
                    <Label>{intl.formatMessage({ id: 'pages.schedule.form.AppointmentForm.clientLabel', defaultMessage: 'Client' })}</Label>
                    <Controller
                        name="client"
                        control={control}
                        rules={{
                            validate: value =>
                                !isMongoId(value)
                                    ? intl.formatMessage(intlMessages.selectClientError)
                                    : true,
                        }}
                        render={({ field: { ref: _ref, value, onChange, name } }) => (
                            <ClientSelect
                                name={name}
                                value={value}
                                onChange={(e, { value: v }) => onChange(v)}
                                change={setValue}
                                search
                                onCloseDialog={onCloseDialog}
                            />
                        )}
                    />
                    {errors.client && (
                        <BaseLabel color="red" pointing basic>{errors.client.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field width={4} error={!!errors.createdAt}>
                    <Label>{intl.formatMessage({ id: 'pages.schedule.form.AppointmentForm.dateLabel', defaultMessage: 'Date' })}</Label>
                    <Controller
                        name="createdAt"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <StyledCalendar
                                value={value}
                                onChange={onChange}
                                format="dd.MM.yyyy"
                                dayPickerProps={{ numberOfMonths: 2, fixedWeeks: true }}
                            />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group>
                <Form.Field width={8} error={!!errors.serialNumber}>
                    <Label>
                        <FormattedMessage
                            id="pages.memberships.form.serialNumber"
                            defaultMessage="Serial number"
                        />
                    </Label>
                    <Controller
                        name="serialNumber"
                        control={control}
                        rules={{
                            validate: value =>
                                value == null || isNotNumber(value.toString())
                                    ? intl.formatMessage(intlMessages.serialNumberError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.serialNumber && (
                        <BaseLabel color="red" pointing basic>{errors.serialNumber.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field width={8} error={!!errors.service}>
                    <Label className="required">
                        {intl.formatMessage({ id: 'pages.schedule.form.AppointmentForm.serviceLabel', defaultMessage: 'Service' })}
                    </Label>
                    <Controller
                        name="service"
                        control={control}
                        rules={{
                            validate: value =>
                                !isMongoId(value)
                                    ? intl.formatMessage(intlMessages.serviceTypeError)
                                    : true,
                        }}
                        render={({ field: { ref: _ref, value, onChange } }) => (
                            <ServiceSelect
                                value={value}
                                options={serviceOptions}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                                onSearch={onServiceSearch}
                                search={onServiceSearch}
                                fluid
                            />
                        )}
                    />
                    {errors.service && (
                        <BaseLabel color="red" pointing basic>{errors.service.message}</BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
            <Form.Group>
                {!createMode && (
                    <Form.Field width={4} error={!!errors.totalUses}>
                        <Label>
                            <FormattedMessage
                                id="pages.memberships.form.numberOfTimesUsed"
                                defaultMessage="Number of times used"
                            />
                        </Label>
                        <Controller
                            name="totalUses"
                            control={control}
                            render={({ field }) => <Input {...field} fluid />}
                        />
                    </Form.Field>
                )}
                <Form.Field width={createMode ? 8 : 4} error={!!errors.quantity}>
                    <Label>
                        <FormattedMessage
                            id="pages.memberships.form.numberOfUses"
                            defaultMessage="Number of uses"
                        />
                    </Label>
                    <Controller
                        name="quantity"
                        control={control}
                        rules={{
                            validate: value =>
                                value == null || isNotNumber(value.toString())
                                    ? intl.formatMessage(intlMessages.quantityError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.quantity && (
                        <BaseLabel color="red" pointing basic>{errors.quantity.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field width={8} error={!!errors.price}>
                    <Label>
                        <FormattedMessage
                            id="pages.inventory.form.ProductForm.price.label"
                            defaultMessage="Price"
                        />
                    </Label>
                    <Controller
                        name="price"
                        control={control}
                        rules={{
                            validate: value =>
                                value == null || priceIsNotValid(value)
                                    ? intl.formatMessage(intlMessages.priceError)
                                    : true,
                        }}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                    {errors.price && (
                        <BaseLabel color="red" pointing basic>{errors.price.message}</BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
        </Form>
    );
}

export default injectIntl(MembershipForm);
